import React, { useCallback, useEffect } from 'react';
import { Check, Loader2, Mic, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cx } from './gitShared';
import { useVoiceInput } from './useVoiceInput';

interface VoiceDictationControlProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  ariaLabel?: string;
}

const VoiceWaveform: React.FC<{ level: number; active: boolean }> = ({ level, active }) => {
  const bars = [0.34, 0.62, 0.46, 0.88, 0.58, 1, 0.72, 0.92, 0.5, 0.76, 0.4, 0.6];
  const amplitude = active ? Math.min(1, Math.max(0.025, level)) : 0.025;
  return (
    <div
      data-testid="voice-dictation-waveform"
      data-level={amplitude.toFixed(3)}
      className="flex h-16 items-center justify-center gap-1.5"
      aria-hidden="true"
    >
      {bars.map((base, i) => (
        <span
          key={i}
          className="block w-1.5 rounded-full bg-white/80 transition-[height,opacity] duration-75 ease-out motion-reduce:transition-none"
          style={{
            height: `${Math.round(4 + amplitude * (18 + base * 38))}px`,
            opacity: active ? 0.48 + amplitude * 0.48 : 0.3,
          }}
        />
      ))}
    </div>
  );
};

const KeyHint: React.FC<{ children: React.ReactNode; inverted?: boolean }> = ({ children, inverted }) => (
  <kbd
    className={cx(
      'rounded border px-1.5 py-0.5 font-mono text-[10px]',
      inverted ? 'border-black/15 bg-black/[0.06] text-black/55' : 'border-white/15 bg-white/[0.06] text-white/55',
    )}
  >
    {children}
  </kbd>
);

const VoiceOverlay: React.FC<{
  level: number;
  state: string;
  onAccept: () => void;
  onCancel: () => void;
}> = ({ level, state, onAccept, onCancel }) => {
  if (typeof document === 'undefined') return null;
  const transcribing = state === 'transcribing';
  return createPortal(
    <div
      data-testid="voice-dictation-overlay"
      className="pointer-events-none fixed inset-0 z-[260] flex items-end justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center sm:pb-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Voice dictation"
        className="pointer-events-auto w-full max-w-md rounded-2xl border border-white/15 bg-black/55 p-4 text-white shadow-2xl shadow-black/45 backdrop-blur-xl sm:p-5"
      >
        <div className="text-center">
          <p className="text-sm font-medium text-white">{transcribing ? 'Transcribing' : 'Listening'}</p>
          <p className="mt-0.5 text-xs text-white/45">
            {transcribing ? 'Turning your recording into text…' : 'Speak naturally — the waveform follows your voice.'}
          </p>
        </div>
        <VoiceWaveform level={level} active={!transcribing} />
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.05] px-3 text-sm text-white/75 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Cancel dictation"
          >
            <X size={15} />
            Cancel
            <KeyHint>Esc</KeyHint>
          </button>
          <button
            type="button"
            onClick={onAccept}
            disabled={transcribing}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-white/15 bg-white px-3 text-sm font-medium text-black transition-colors hover:bg-white/90 disabled:cursor-wait disabled:opacity-65"
            aria-label="Accept dictation"
          >
            {transcribing ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            {transcribing ? 'Working' : 'Accept'}
            {!transcribing && <KeyHint inverted>Enter</KeyHint>}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const VoiceDictationControl: React.FC<VoiceDictationControlProps> = ({
  value,
  onChange,
  disabled,
  className,
  buttonClassName,
  ariaLabel = 'Dictate',
}) => {
  const voice = useVoiceInput();
  const active = voice.state !== 'idle';

  const toggle = useCallback(() => {
    if (voice.state !== 'idle') {
      voice.stop();
      return;
    }
    voice.start(value, onChange);
  }, [voice, value, onChange]);

  useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const plainEnter = event.key === 'Enter' && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey;
      const escape = event.key === 'Escape';
      if (!plainEnter && !escape) return;
      event.preventDefault();
      event.stopPropagation();
      if (escape) voice.cancel();
      else if (voice.state === 'recording') voice.stop();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [active, voice.cancel, voice.state, voice.stop]);

  if (!voice.supported) return null;

  return (
    <span className={cx('inline-flex shrink-0', className)}>
      <button
        data-testid="voice-dictation-button"
        type="button"
        onClick={toggle}
        disabled={disabled || voice.state === 'transcribing'}
        aria-label={voice.state === 'recording' ? 'Stop dictation' : ariaLabel}
        title={voice.state === 'recording' ? 'Stop dictation' : ariaLabel}
        className={cx(
          'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-45',
          voice.state === 'recording'
            ? 'border-rose-400/70 bg-rose-400/15 text-rose-100'
            : voice.state === 'transcribing'
              ? 'border-white/10 bg-black text-white/40'
              : 'border-white/10 bg-black text-white/65 hover:bg-white/[0.06] hover:text-white',
          buttonClassName,
        )}
        style={
          voice.state === 'recording'
            ? { boxShadow: `0 0 0 ${Math.round(2 + voice.level * 10)}px rgba(244,63,94,0.14)` }
            : undefined
        }
      >
        {voice.state === 'transcribing' ? <Loader2 size={17} className="animate-spin" /> : <Mic size={17} />}
      </button>
      {active && (
        <VoiceOverlay
          level={voice.level}
          state={voice.state}
          onAccept={voice.stop}
          onCancel={voice.cancel}
        />
      )}
    </span>
  );
};
